// @flow

import _ from 'lodash';

/**
 * Single read point for 3rd-party integration flags.
 *
 * `tenantApp.currentAppIntegration` (written by the backend, mirrored by the
 * storefront as `window.boostWidgetIntegration.integration`) is the source of
 * truth. Do not read legacy copies of the same state.
 * See docs/third-party-integrations.md.
 *
 * Kept free of src/constants/integration imports on purpose: that module pulls
 * in React card components.
 */

// Shared empty value so selectors / mapStateToProps keep a stable reference
const EMPTY_APP_INTEGRATION = Object.freeze({});

/**
 * Get the whole currentAppIntegration object
 *
 * @param  {Object} tenantApp tenant app
 *
 * @return {Object}           currentAppIntegration, a shared frozen {} when missing
 */
export function getCurrentAppIntegration(tenantApp: ?Object): Object {
  return _.get(tenantApp, 'currentAppIntegration') || EMPTY_APP_INTEGRATION;
}

/**
 * Get the app integrated for an app type (e.g. wishlists -> 'smart')
 *
 * @param  {Object} tenantApp tenant app
 * @param  {string} appType   app type, see AppType in src/constants/integration
 *
 * @return {string}           app name, '' when none
 */
export function getIntegratedApp(tenantApp: ?Object, appType: string): string {
  const appName = getCurrentAppIntegration(tenantApp)[appType];
  return typeof appName === 'string' ? appName : '';
}

/**
 * Check whether an app is the one integrated for its app type
 *
 * @param  {Object} tenantApp tenant app
 * @param  {string} appType   app type
 * @param  {string} appName   app name
 *
 * @return {boolean}
 */
export function isAppIntegrated(tenantApp: ?Object, appType: string, appName: string): boolean {
  return !!appName && getIntegratedApp(tenantApp, appType) === appName;
}
